import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  Form,
  Input,
  Row,
  Spin,
  Typography,
  Upload,
  Select,
  Checkbox,
  message,
} from "antd";
import {
  DeleteOutlined,
  LoadingOutlined,
  PaperClipOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { CheckboxChangeEvent } from "antd/es/checkbox";
import { useParams } from "react-router-dom";
import moment from "moment";

import {
  MAX_OFFER_REQUEST_FILES,
  getFileUrl,
  addEditOfferFormLayout as layout,
  scopeOfCoverages,
} from "../../../utils";
import {
  addEditRequestProps,
  addEditRequestType,
  attachmentType,
  offerRequestFileType,
} from "../../../models";

const { TextArea } = Input;

type AttachmentsInputProps = {
  value?: attachmentType[];
  onChange?: (value: attachmentType[]) => void;
  onDownload: (file: offerRequestFileType) => void;
};

// Controlled by the surrounding Form.Item, which injects value/onChange.
const AttachmentsInput = ({
  value = [],
  onChange,
  onDownload,
}: AttachmentsInputProps) => {
  const addFiles = (file: File, fileList: File[]) => {
    // beforeUpload runs once per file of a multi-select; handle the whole
    // batch on the first call so the other calls don't read a stale value.
    if (file !== fileList[0]) {
      return false;
    }
    const freeSlots = MAX_OFFER_REQUEST_FILES - value.length;
    if (fileList.length > freeSlots) {
      message.warning(
        `Možete dodati najviše ${MAX_OFFER_REQUEST_FILES} priloga.`
      );
    }
    onChange?.([...value, ...fileList.slice(0, Math.max(freeSlots, 0))]);
    return false;
  };

  const removeFile = (index: number) => {
    onChange?.(value.filter((_, i) => i !== index));
  };

  return (
    <>
      {value.map((file, index) => (
        <div
          key={file instanceof File ? `${file.name}-${index}` : file.id}
          className="flex justify-between items-center bg-light-gray mt-2"
        >
          <div className="flex w-full pl-1">
            <PaperClipOutlined />
            {file instanceof File ? (
              <a
                className="text-primary ml-2 w-4/5"
                href={getFileUrl(file)}
                download={file.name}
              >
                {file.name}
              </a>
            ) : (
              <div
                className="text-primary ml-2 w-4/5 cursor-pointer"
                onClick={() => onDownload(file)}
              >
                {file.originalName}
              </div>
            )}
          </div>
          <DeleteOutlined
            className="text-black opacity-40 pr-2 cursor-pointer"
            onClick={() => removeFile(index)}
          />
        </div>
      ))}
      {value.length < MAX_OFFER_REQUEST_FILES && (
        <Upload
          beforeUpload={addFiles}
          multiple
          showUploadList={false}
          accept=".pdf"
        >
          <Button
            className="rounded-sm border-inherit text-inherit mt-2"
            icon={<SettingOutlined />}
          >
            Klikni da dodaš
          </Button>
        </Upload>
      )}
    </>
  );
};

const AddEditForm = ({ isAdd, actions, state }: addEditRequestProps) => {
  const {
    loadingSave,
    loadingSend,
    currentOfferRequest,
    currentOfferRequestSent,
    insurances = [],
  } = state;
  const { id } = useParams();

  const [form] = Form.useForm();
  const [formInsurance] = Form.useForm();

  const [checkedAll, setIscheckedAll] = useState(false);

  useEffect(() => {
    if (!id || !actions.getRequestById) {
      return;
    }

    actions.getRequestById(id);
    // Clear the insurance selection only when opening another request, not
    // when the same request is reloaded after saving.
    formInsurance.setFieldsValue({
      insurance: [],
    });
    setIscheckedAll(false);
  }, [id]);

  useEffect(() => {
    if (!actions.getInsurances) {
      return;
    }

    actions.getInsurances();
  }, []);

  useEffect(() => {
    if (isAdd) {
      return;
    }
    form.setFieldsValue(currentOfferRequest);
  }, [currentOfferRequest]);

  const sentRecords =
    currentOfferRequestSent ?? currentOfferRequest?.offerRequestSent ?? [];
  const sentInsuranceIds = sentRecords.map((record) => record.insuranceId);

  const onFinishOffer = (values: addEditRequestType) => {
    const files = values.files ?? [];
    const newFiles = files.filter((file): file is File => file instanceof File);
    if (isAdd) {
      actions.addRequest?.(newFiles, values);
      return;
    }
    const keptFileIds = files
      .filter((file): file is offerRequestFileType => !(file instanceof File))
      .map((file) => file.id);
    const removedFileIds = (currentOfferRequest?.files ?? [])
      .filter((file): file is offerRequestFileType => !(file instanceof File))
      .map((file) => file.id)
      .filter((fileId) => !keptFileIds.includes(fileId));
    id && actions.editRequest?.(newFiles, removedFileIds, values, id);
  };

  const onFinishInsurance = ({ insurance }: { insurance: string[] }) => {
    if (!id || !actions.sendRequest) {
      return;
    }

    actions.sendRequest(id, insurance);
  };

  const onCheckAllChange = (e: CheckboxChangeEvent) => {
    formInsurance.setFieldsValue({
      insurance: e.target.checked ? insurances.map((ins) => ins.key) : [],
    });
    setIscheckedAll(e.target.checked);
  };

  const onChange = () => {
    setIscheckedAll(
      formInsurance.getFieldsValue().insurance.length === insurances.length
    );
  };

  const handleFileDownload = (file: offerRequestFileType) => {
    if (actions.downloadFile && id) {
      actions.downloadFile(id, file.id, file.originalName);
    }
  };

  return (
    <>
      <div className="px-8 py-4">
        <Typography className="text-xl font-bold text-secondary py-1">
          {isAdd
            ? "KREIRANJE ZAHTJEVA ZA PONUDU ZA OSIGURANJE"
            : currentOfferRequest?.name}
        </Typography>
      </div>
      <div className="h-5 bg-base-gray"></div>

      <div className="p-8">
        <Row gutter={20}>
          <Col
            span={24}
            md={20}
            lg={!isAdd ? 13 : 17}
            className="mb-12 lg:mb-0"
          >
            <Form
              {...layout(isAdd)}
              colon={false}
              form={form}
              onFinish={onFinishOffer}
            >
              {isAdd && (
                <Form.Item
                  label="Naziv zahtjeva za ponudu"
                  name="name"
                  rules={[
                    {
                      required: true,
                      message: "Unesite naziv zahtjeva za ponudu",
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              )}

              <Form.Item
                label="Klijent"
                name="client"
                rules={[
                  {
                    required: true,
                    message: "Unesite naziv klijenta",
                  },
                ]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                label="ID broj"
                name="IDNumber"
                rules={[
                  {
                    required: true,
                    message: "Unesite ID broj",
                  },
                ]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                label="Adresa / sjedište klijenta"
                name="address"
                rules={[
                  {
                    required: true,
                    message: "Unesite adresu / sjedište klijenta",
                  },
                ]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                label="Kontakt telefon"
                name="phoneNumber"
                rules={[
                  {
                    required: true,
                    message: "Unesite kontakt telefon",
                  },
                ]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                label="Prilozi"
                name="files"
                rules={[
                  {
                    type: "array",
                    required: true,
                    message: "Dodajte prilog",
                  },
                ]}
              >
                <AttachmentsInput onDownload={handleFileDownload} />
              </Form.Item>

              {!isAdd && (
                <Form.Item
                  label="Obim pokrića"
                  name="scopeOfCoverage"
                  rules={[
                    {
                      required: true,
                      message: "Odaberite obim pokrića",
                    },
                  ]}
                >
                  <Select
                    options={scopeOfCoverages.map((scope) => ({
                      label: scope,
                      value: scope,
                    }))}
                  />
                </Form.Item>
              )}

              <Form.Item name="note" label="Dodatne napomene">
                <TextArea rows={6} />
              </Form.Item>

              <div className="sm:flex mt-6 lg:mt-20 ml-0 md:ml-6 lg:ml-0">
                <Form.Item>
                  <Button
                    disabled={loadingSave || loadingSend}
                    type="primary"
                    htmlType="submit"
                    className="bg-primary font-medium w-full sm:w-[132px] rounded-sm mr-8 "
                  >
                    SPREMI
                  </Button>
                </Form.Item>

                {loadingSave && (
                  <Spin
                    className="w-full sm:w-fit"
                    indicator={
                      <LoadingOutlined style={{ fontSize: 32 }} spin />
                    }
                  />
                )}
              </div>
            </Form>
          </Col>

          {!isAdd && (
            <Col span={24} md={19} lg={10} className="ml-0 md:ml-6">
              <Form
                colon={false}
                form={formInsurance}
                onFinish={onFinishInsurance}
              >
                {sentRecords.length > 0 && (
                  <div className="mb-6 bg-light-gray px-4 py-3 rounded-sm">
                    <Typography className="font-bold mb-1">
                      VEĆ POSLANO:
                    </Typography>
                    {sentRecords.map((record) => (
                      <div key={record.insuranceId} className="text-sm">
                        {record.insurance?.name} –{" "}
                        {moment(record.createdAt).format("DD.MM.YYYY HH:mm")}
                        {record.sentBy && `, poslao: ${record.sentBy.name}`}
                      </div>
                    ))}
                  </div>
                )}
                <Typography className=" text-[16px] font-normal">
                  ODABERI OSIGURAVAJUĆA DRUŠTVA KOJIM ŽELIŠ POSLATI ZAHTJEV ZA
                  PONUDU
                </Typography>
                <Checkbox
                  className="ml-[33px] my-3"
                  onChange={onCheckAllChange}
                  checked={checkedAll}
                >
                  OZNAČI SVE
                </Checkbox>
                <Form.Item
                  name="insurance"
                  rules={[
                    {
                      required: true,
                      message: "Odaberi osiguravajuća društva!",
                    },
                  ]}
                >
                  <Checkbox.Group
                    onChange={onChange}
                    className="gap-1 bg-dashed-box border border-dashed border-offer-blue rounded-md px-8 py-5"
                    options={insurances?.map((company) => ({
                      label: sentInsuranceIds.includes(company.key.toString())
                        ? `${company.name} (poslano)`
                        : company.name,
                      value: company.key.toString(),
                    }))}
                  />
                </Form.Item>
                <div className="sm:flex mt-6 lg:mt-20 ml-0 md:ml-6 lg:ml-0">
                  <Form.Item>
                    <Button
                      disabled={loadingSave || loadingSend}
                      type="primary"
                      htmlType="submit"
                      className="bg-primary font-medium w-full sm:w-[132px] rounded-sm mr-8"
                    >
                      POŠALJI
                    </Button>
                  </Form.Item>
                  {loadingSend && (
                    <Spin
                      className="w-full sm:w-fit"
                      indicator={
                        <LoadingOutlined style={{ fontSize: 32 }} spin />
                      }
                    />
                  )}
                </div>
              </Form>
            </Col>
          )}
        </Row>
      </div>
    </>
  );
};

export default AddEditForm;
